import { useEffect, useMemo, useState } from 'react';
import { listChatModels, type ChatModelDescriptor } from '../api/models';

// Shared hook — fetches /api/models once per mount, groups by provider.
// Used by MatrixLab (picker), Evaluations (filter), Dashboard (scorecard/best-by-role).

export interface UseChatModelsResult {
  models: ChatModelDescriptor[];
  byProvider: Map<string, ChatModelDescriptor[]>;
  byChatModelId: Map<string, ChatModelDescriptor>;
  loading: boolean;
  error: string | null;
}

export function useChatModels(configuredOnly = true): UseChatModelsResult {
  const [models, setModels] = useState<ChatModelDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listChatModels(configuredOnly)
      .then(list => { if (!cancelled) { setModels(list); setError(null); } })
      .catch(e => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [configuredOnly]);

  const byProvider = useMemo(() => {
    const m = new Map<string, ChatModelDescriptor[]>();
    for (const d of models) {
      const arr = m.get(d.providerId) ?? [];
      arr.push(d);
      m.set(d.providerId, arr);
    }
    return m;
  }, [models]);

  const byChatModelId = useMemo(() => {
    const m = new Map<string, ChatModelDescriptor>();
    for (const d of models) m.set(d.chatModelId, d);
    return m;
  }, [models]);

  return { models, byProvider, byChatModelId, loading, error };
}
